/**
 * sdfLibrary.ts — visual catalogue of the 2D SDF library.
 *
 * Renders one labeled example of every shape in src/sdfParametric.ts (the
 * same set covered by the rustadventure SDF tutorial). Each tile is a real
 * SDF node driven by `makeSdf` — the rect pipeline dispatches the per-instance
 * `sdfKind` to the matching formula in src/gl/rectPipeline.ts.
 *
 * The point: proof that the engine's SDF library compiles, links, and
 * actually produces the shapes the formulas describe. Anything you can
 * build here, you can drop into any other scene.
 *
 * Layout: 5 columns × 3 rows. Tile is 220×220 CSS px with a 28-px label band.
 * The first row shows primitives, the second row curves/strokes, the third
 * row combinations.
 */

import {
  type Scene,
  createScene,
  makeRect,
  makeText,
  makeSdf,
  addNode,
} from "../sceneGraph";
import { SDF_LIBRARY, type SdfEntry } from "../sdfParametric";

const VW = 1280;
const VH = 600;  // landscape, fits most viewports — 2 rows of tiles
const COLS = 4;
const TILE = 180;
const GAP = 32;
const LABEL_H = 28;
const TOP_PAD = 90;
const LEFT_PAD = (VW - (COLS * TILE + (COLS - 1) * GAP)) / 2;

const PALETTE = {
  bg: { r: 0.05, g: 0.05, b: 0.07, a: 1 },
  panel: { r: 0.09, g: 0.09, b: 0.13, a: 1 },
  panelStroke: { r: 0.15, g: 0.15, b: 0.20, a: 1 },
  primary: { r: 0.42, g: 0.85, b: 0.95, a: 1 },
  accent: { r: 0.96, g: 0.55, b: 0.20, a: 1 },
  text: { r: 0.92, g: 0.92, b: 0.96, a: 1 },
  textDim: { r: 0.55, g: 0.55, b: 0.65, a: 1 },
};

/** Per-tile visual config — chooses the kind, color and parameter
 *  preset that produces the most representative render for each SDF. */
const TILES: Array<{ entry: SdfEntry; color: { r: number; g: number; b: number; a: number }; build: () => { sdfKind: any; sdfParamsA: any; sdfParamsB: any }; stroke: number }> = [
  // === Row 1: Classic + IQ favorites ===
  { entry: SDF_LIBRARY[0]!,  // circle
    color: PALETTE.primary, stroke: 0,
    build: () => ({ sdfKind: "circle", sdfParamsA: [75, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[6]!,  // heart
    color: { r: 0.95, g: 0.30, b: 0.45, a: 1 }, stroke: 0,
    build: () => ({ sdfKind: "heart", sdfParamsA: [95, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[5]!,  // 5-point star
    color: PALETTE.accent, stroke: 0,
    build: () => ({ sdfKind: "star", sdfParamsA: [85, 0.42], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[15]!, // equilateral triangle
    color: PALETTE.primary, stroke: 0,
    build: () => ({ sdfKind: "equilateralTri", sdfParamsA: [70, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] }) },

  // === Row 2: NEW inventions — visually striking ===
  { entry: SDF_LIBRARY[32]!, // cloud (NEW)
    color: PALETTE.primary, stroke: 0,
    build: () => ({ sdfKind: "cloud", sdfParamsA: [62, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[33]!, // lightning (NEW)
    color: PALETTE.accent, stroke: 0,
    build: () => ({ sdfKind: "lightning", sdfParamsA: [68, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[29]!, // gear (NEW)
    color: PALETTE.primary, stroke: 0,
    build: () => ({ sdfKind: "gear", sdfParamsA: [62, 10, 10], sdfParamsB: [0, 0, 0, 0] }) },
  { entry: SDF_LIBRARY[42]!, // flower (NEW)
    color: { r: 0.85, g: 0.55, b: 0.95, a: 1 }, stroke: 0,
    build: () => ({ sdfKind: "flower", sdfParamsA: [70, 6], sdfParamsB: [0, 0, 0, 0] }) },
];

export function sdfLibraryScene(_w: number, _h: number): Scene {
  const scene = createScene();

  // Background.
  addNode(scene, makeRect({
    x: 0, y: 0, w: VW, h: VH,
    fill: PALETTE.bg,
  }));

  // Header.
  addNode(scene, makeText({
    x: 64, y: 36, w: 700, h: 28,
    text: "SDF Library",
    fontSize: 26, fontWeight: 800,
    textColor: PALETTE.text,
    letterSpacing: -0.4,
    fontFamily: "system-ui, -apple-system, sans-serif",
  }));
  addNode(scene, makeText({
    x: 64, y: 70, w: 900, h: 18,
    text: "Inigo Quilez 2D SDFs · the same set covered by the rustadventure tutorial",
    fontSize: 12, fontWeight: 500,
    textColor: PALETTE.textDim,
    letterSpacing: 0.2,
    fontFamily: "system-ui, -apple-system, sans-serif",
  }));

  // Tile grid.
  for (let i = 0; i < TILES.length; i++) {
    const tile = TILES[i]!;
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const tx = LEFT_PAD + col * (TILE + GAP);
    const ty = TOP_PAD + row * (TILE + LABEL_H + GAP);

    // Panel background.
    addNode(scene, makeRect({
      x: tx, y: ty, w: TILE, h: TILE,
      fill: PALETTE.panel,
      radius: 8,
      strokeWidth: 1,
      stroke: PALETTE.panelStroke,
    }));

    // The shape itself. makeSdf routes the sdfKind / sdfParamsA/B fields
    // through to the compiler so the rect pipeline's fragment shader can
    // dispatch to the right IQ distance function.
    addNode(scene, makeSdf({
      x: tx, y: ty, w: TILE, h: TILE,
      fill: tile.color,
      radius: 0,
      ...tile.build(),
    }));

    // Label band beneath the panel.
    addNode(scene, makeText({
      x: tx, y: ty + TILE + 6, w: TILE, h: 14,
      text: tile.entry.name,
      fontSize: 11, fontWeight: 700,
      textColor: PALETTE.text,
      letterSpacing: 0.2,
      textAlign: "center",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }));
    addNode(scene, makeText({
      x: tx, y: ty + TILE + 22, w: TILE, h: 12,
      text: tile.entry.formula,
      fontSize: 9, fontWeight: 500,
      textColor: PALETTE.textDim,
      letterSpacing: 0.1,
      textAlign: "center",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    }));
  }

  // Footer.
  addNode(scene, makeText({
    x: 64, y: VH - 36, w: VW - 128, h: 16,
    text: "src/sdfParametric.ts · src/scenes/sdfLibrary.ts",
    fontSize: 11, fontWeight: 500,
    textColor: PALETTE.textDim,
    letterSpacing: 0.2,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  }));

  return scene;
}
